import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ErroAPI, api, desde } from "../api.ts";
import { Cartao, MicroMedidor, SeloSituacao, Vazio } from "../componentes/basicos.tsx";
import { podeEscrever, useSessao } from "../sessao.tsx";
import { useDados } from "../usarDados.ts";

type Empresa = {
  id: number; razao_social: string; fantasia: string; cnpj: string;
  licencas_ativas: number; licencas_outras: number;
  maquinas_ativas: number; vagas: number;
  ultimo_sinal: string | null; situacao: string; ativo: boolean;
};

const FILTROS: [string, string][] = [
  ["", "Todas"],
  ["atencao", "Atencao"],
  ["silenciosa", "Silenciosas"],
  ["nunca_ativou", "Nunca ativaram"],
  ["arquivada", "Arquivadas"],
];

/**
 * A lista de empresas e o lugar onde "quem esta morto" precisa aparecer num
 * relance. Antes tinha cinco colunas e nenhum sinal de saude: uma empresa
 * parada ha oito meses era indistinguivel da mais ativa.
 *
 * O indicador que faz mais trabalho com menos tinta e o ULTIMO SINAL EM PROSA:
 * "ha 8 meses" ao lado de "ha 2 horas" dispensa cor. A situacao acrescenta so a
 * interpretacao -- "nunca ativou" nao e o mesmo que "silenciosa", embora as
 * duas leiam parecido numa lista.
 */
export function Empresas() {
  const { usuario } = useSessao();
  const [busca, setBusca] = useState("");
  const [situacao, setSituacao] = useState("");
  const [criando, setCriando] = useState(false);
  const arquivadas = situacao === "arquivada";

  const q = new URLSearchParams();
  if (busca) q.set("q", busca);
  if (situacao) q.set("situacao", situacao);
  if (arquivadas) q.set("arquivadas", "1");

  const { dados, erro, carregando } = useDados<Empresa[]>(`/empresas?${q}`);

  return (
    <>
      <div className="entre" style={{ marginBottom: "var(--e-lg)" }}>
        <h1 className="mb-0">Empresas</h1>
        {podeEscrever(usuario) && (
          <button className="botao" type="button" onClick={() => setCriando((v) => !v)}>
            {criando ? "Cancelar" : "Nova empresa"}
          </button>
        )}
      </div>

      {criando && <NovaEmpresa aoCriar={() => setCriando(false)} />}

      <Cartao>
        <div className="linha" style={{ marginBottom: "var(--e-md)" }}>
          <input className="entrada" type="search" value={busca} style={{ flex: 1, minWidth: 240 }}
                 placeholder="Buscar por razao social, fantasia ou CNPJ"
                 onChange={(e) => setBusca(e.target.value)} />
        </div>
        <div className="chips" style={{ marginBottom: "var(--e-md)" }}>
          {FILTROS.map(([valor, rotulo]) => (
            <button key={valor} type="button"
                    className={`chip${situacao === valor ? " chip--ativo" : ""}`}
                    onClick={() => setSituacao(valor)}>{rotulo}</button>
          ))}
        </div>

        {carregando && <div className="carregando">carregando...</div>}
        {erro && <div className="alerta alerta--erro">{erro}</div>}

        {dados && dados.length === 0 && (
          <Vazio titulo="Nenhuma empresa encontrada">
            {busca ? `Nada bate com "${busca}".` : "Ajuste o filtro ou cadastre a primeira."}
          </Vazio>
        )}

        {dados && dados.length > 0 && (
          <div className="tabela-rolagem">
            <table className="tabela">
              <thead>
                <tr>
                  <th>Empresa</th><th>CNPJ</th><th>Licencas</th>
                  <th>Maquinas</th><th>Ocupacao</th><th>Ultimo sinal</th><th>Situacao</th>
                </tr>
              </thead>
              <tbody>
                {dados.map((e) => (
                  <tr key={e.id}>
                    <td>
                      <Link to={`/empresas/${e.id}`}>{e.razao_social}</Link>
                      {e.fantasia && <span className="suave pequeno"> &middot; {e.fantasia}</span>}
                    </td>
                    <td className="tabela__mono">{e.cnpj}</td>
                    <td className="pequeno">
                      {e.licencas_ativas} ativa{e.licencas_ativas === 1 ? "" : "s"}
                      {e.licencas_outras > 0 && (
                        <span className="apagado"> &middot; {e.licencas_outras} parada(s)</span>
                      )}
                    </td>
                    <td className="tabela__num">{e.maquinas_ativas}</td>
                    <td><MicroMedidor usado={e.maquinas_ativas} total={e.vagas} /></td>
                    <td className="suave pequeno">{desde(e.ultimo_sinal)}</td>
                    <td><SeloSituacao situacao={e.situacao} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="micro apagado mt-lg mb-0">
          Ordenado por saude: pior primeiro, depois quem esta calado ha mais tempo.
        </p>
      </Cartao>
    </>
  );
}

/**
 * Cadastro de empresa nova.
 *
 * Razao social e CNPJ bastam, e e proposital: o cadastro completo pode esperar,
 * a licenca nao. Quem esta com o tecnico do cliente no telefone precisa chegar
 * ao codigo de ativacao em poucos cliques, e nao preencher oito campos antes.
 *
 * Ao gravar, vai direto para a ficha da empresa -- que e onde a licenca nasce e
 * o codigo e gerado. Sem isso o operador cadastra e fica olhando para a lista,
 * sem saber que falta a licenca.
 */
function NovaEmpresa({ aoCriar }: { aoCriar: () => void }) {
  const navegar = useNavigate();
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  return (
    <Cartao titulo="Nova empresa">
      {erro && <div className="alerta alerta--erro">{erro}</div>}
      <form onSubmit={(ev) => {
        ev.preventDefault();
        const f = new FormData(ev.currentTarget);
        setErro(""); setSalvando(true);
        void (async () => {
          try {
            const r = await api.post<{ id: number }>("/empresas", {
              razao_social: f.get("razao_social"), fantasia: f.get("fantasia"),
              cnpj: f.get("cnpj"), email: f.get("email"), telefone: f.get("telefone"),
              cidade: f.get("cidade"), uf: f.get("uf"),
            });
            aoCriar();
            navegar(`/empresas/${r.id}`);
          } catch (e) {
            setErro(e instanceof ErroAPI ? e.message : "Nao consegui cadastrar.");
            setSalvando(false);
          }
        })();
      }}>
        <div className="grade grade--2">
          <label className="campo"><span className="campo__rotulo">Razao social</span>
            <input type="text" name="razao_social" required autoFocus /></label>
          <label className="campo"><span className="campo__rotulo">CNPJ</span>
            <input type="text" name="cnpj" required placeholder="00.000.000/0001-00" /></label>
        </div>
        <div className="grade grade--3">
          <label className="campo"><span className="campo__rotulo">Nome fantasia</span>
            <input type="text" name="fantasia" /></label>
          <label className="campo"><span className="campo__rotulo">E-mail</span>
            <input type="email" name="email" />
            <span className="campo__dica">Para onde vao os avisos de vencimento.</span></label>
          <label className="campo"><span className="campo__rotulo">Telefone</span>
            <input type="text" name="telefone" />
            <span className="campo__dica">E o que o alerta manda voce usar.</span></label>
        </div>
        <div className="grade grade--3">
          <label className="campo"><span className="campo__rotulo">Cidade</span>
            <input type="text" name="cidade" /></label>
          <label className="campo"><span className="campo__rotulo">UF</span>
            <input type="text" name="uf" maxLength={2} /></label>
          <div style={{ alignSelf: "end" }}>
            <button className="botao" type="submit" disabled={salvando}>
              {salvando ? "Cadastrando..." : "Cadastrar e abrir"}
            </button>
          </div>
        </div>
      </form>
    </Cartao>
  );
}
